import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined'
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined'
import WarehouseOutlinedIcon from '@mui/icons-material/WarehouseOutlined'
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined'
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined'
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined'
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined'
import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineSeparator,
} from '@mui/lab'
import { Typography, Box } from '@mui/material'
import { FC, useEffect, useMemo, useState } from 'react'
import { Placeholder } from '../Placeholder/placeholder.component'
import { Loader } from '../Loader'
import { IOrderAuditTimelineEvent } from '../../Services/api/audit/audit.interface'
import { GetOrderAuditTimelineUseCase } from '../../Usecase/Audit/get-order-audit-timeline-usecase'
import {
  formatAuditEventCategory,
  formatAuditEventDetails,
  formatAuditEventTitle,
  prepareAuditEventsForDisplay,
} from '../../utils/format-audit-timeline-event'
import { Tag } from '../Tag/tag.component'

interface IOrderHistoryTimelineProps {
  orderId?: string
  nsOrderId?: string
  createdAt?: string
  getOrderAuditTimelineUseCase: GetOrderAuditTimelineUseCase
  maxHeight?: number | string
}

type TimelineDotColor = 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info'

function formatTimelineDate(value?: string): string {
  if (!value) return ''
  try {
    return new Date(value).toLocaleString('pt-BR')
  } catch {
    return value
  }
}

function buildFallbackEvents(createdAt?: string): IOrderAuditTimelineEvent[] {
  if (!createdAt) return []

  return [{
    timestamp: createdAt,
    eventType: 'ORDER_CHANGE',
    action: 'CREATE',
    description: 'Pedido registrado no Hub',
    userName: undefined,
  }]
}

function formatEventSubtitle(event: IOrderAuditTimelineEvent): string {
  const parts: string[] = []

  if (event.userName) {
    parts.push(`Por ${event.userName}`)
  }

  const dateLabel = formatTimelineDate(event.timestamp)
  if (dateLabel) {
    parts.push(dateLabel)
  }

  return parts.join(' · ')
}

function getEventTimelineVisual(event: IOrderAuditTimelineEvent): {
  icon: FC<{ fontSize?: 'small' | 'inherit' | 'medium' | 'large' }>
  dotColor: TimelineDotColor
} {
  switch (event.eventType) {
    case 'ORDER_CHANGE':
      return { icon: ShoppingCartOutlinedIcon, dotColor: 'primary' }
    case 'PRODUCT_CHANGE':
      return { icon: Inventory2OutlinedIcon, dotColor: 'info' }
    case 'STOCK_MOVEMENT':
      return { icon: WarehouseOutlinedIcon, dotColor: 'warning' }
    case 'PAYMENT_CHANGE':
      return { icon: PaymentsOutlinedIcon, dotColor: 'success' }
    case 'BILLING_CHANGE':
      return { icon: ReceiptLongOutlinedIcon, dotColor: 'secondary' }
    case 'SHIPPING_CHANGE':
      return { icon: LocalShippingOutlinedIcon, dotColor: 'info' }
    case 'ADDRESS_CHANGE':
      return { icon: PlaceOutlinedIcon, dotColor: 'secondary' }
    case 'CUSTOMER_CHANGE':
    case 'SELLER_CHANGE':
      return { icon: PersonOutlineIcon, dotColor: 'secondary' }
    default:
      return { icon: HistoryOutlinedIcon, dotColor: 'primary' }
  }
}

export const OrderHistoryTimeline: FC<IOrderHistoryTimelineProps> = ({
  orderId,
  nsOrderId,
  createdAt,
  getOrderAuditTimelineUseCase,
  maxHeight = 360,
}) => {
  const [loading, setLoading] = useState(false)
  const [events, setEvents] = useState<IOrderAuditTimelineEvent[]>([])

  useEffect(() => {
    if (!orderId && !nsOrderId) {
      setEvents(buildFallbackEvents(createdAt))
      return
    }

    let cancelled = false

    const loadTimeline = async () => {
      setLoading(true)

      const result = await getOrderAuditTimelineUseCase.handle({
        orderId,
        nsOrderId,
      })

      if (cancelled) return

      if (!result.isFailure) {
        const timelineEvents = result.getValue()?.events ?? []
        setEvents(timelineEvents.length ? timelineEvents : buildFallbackEvents(createdAt))
      } else {
        setEvents(buildFallbackEvents(createdAt))
      }

      setLoading(false)
    }

    void loadTimeline()

    return () => {
      cancelled = true
    }
  }, [orderId, nsOrderId, createdAt, getOrderAuditTimelineUseCase])

  const sortedEvents = useMemo(
    () => prepareAuditEventsForDisplay(events),
    [events],
  )

  if (loading) {
    return <Loader />
  }

  if (!sortedEvents.length) {
    return <Placeholder description="Nenhum histórico disponível para este pedido." />
  }

  return (
    <Box
      sx={{
        maxHeight,
        overflowY: 'auto',
        overflowX: 'hidden',
        pr: 0.5,
        scrollbarWidth: 'thin',
        '&::-webkit-scrollbar': {
          width: 6,
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgba(0,0,0,0.2)',
          borderRadius: 3,
        },
      }}
    >
      <Timeline
        position="right"
        sx={{
          m: 0,
          p: 0,
          '& .MuiTimelineItem-root:before': {
            flex: 0,
            padding: 0,
          },
        }}
      >
        {sortedEvents.map((event, index) => {
          const { icon: EventIcon, dotColor } = getEventTimelineVisual(event)

          return (
            <TimelineItem key={`${event.timestamp}-${event.description}-${index}`}>
              <TimelineSeparator>
                {index > 0 && <TimelineConnector />}
                <TimelineDot color={dotColor} variant="outlined">
                  <EventIcon fontSize="small" />
                </TimelineDot>
                {index < sortedEvents.length - 1 && (
                  <TimelineConnector sx={{ bgcolor: 'divider' }} />
                )}
              </TimelineSeparator>
              <TimelineContent sx={{ py: 1.5, pr: 0, pb: index === sortedEvents.length - 1 ? 0 : 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                  <Tag
                    title={formatAuditEventCategory(event)}
                    boxStyle={{
                      backgroundColor: 'rgba(33, 150, 243, 0.12)',
                      borderRadius: '6px',
                      height: '22px',
                      padding: '0 8px',
                    }}
                    titleStyle={{
                      color: '#1565c0',
                      fontSize: '0.7rem',
                      fontWeight: 700,
                    }}
                  />
                  <Typography variant="subtitle2" component="span" sx={{ fontWeight: 600, lineHeight: 1.35 }}>
                    {formatAuditEventTitle(event)}
                  </Typography>
                </Box>

                {formatAuditEventDetails(event).map((detail, detailIndex) => (
                  <Typography
                    key={`${event.timestamp}-detail-${detailIndex}`}
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mt: 0.25,
                      wordBreak: 'break-word',
                      lineHeight: 1.4,
                    }}
                  >
                    {detail}
                  </Typography>
                ))}

                {formatEventSubtitle(event) && (
                  <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.75 }}>
                    {formatEventSubtitle(event)}
                  </Typography>
                )}
              </TimelineContent>
            </TimelineItem>
          )
        })}
      </Timeline>
    </Box>
  )
}
